import type { Middleware } from 'openapi-fetch';

export const API_VERSION = '1';
export const IDEMPOTENCY_HEADER = 'Idempotency-Key';
const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export interface VersionHeaderOptions {
  /** The web app's build id; sent so the API can tell clients apart in logs (never content). */
  clientVersion: string;
}

/** Every request says which API version it speaks and which client build it comes from. */
export function versionHeaders(opts: VersionHeaderOptions): Middleware {
  return {
    onRequest({ request }) {
      request.headers.set('Paige-Version', API_VERSION);
      request.headers.set('X-Paige-Client', `web/${opts.clientVersion}`);
      request.headers.set('Accept', 'application/json');
      return request;
    },
  };
}

/**
 * Every mutation carries an Idempotency-Key (architecture §12.1, §14.1). A caller that retries
 * the same logical operation passes its own key; otherwise a fresh UUID is minted per request.
 */
export function idempotencyKeys(newKey: () => string = () => crypto.randomUUID()): Middleware {
  return {
    onRequest({ request }) {
      if (!MUTATING.has(request.method.toUpperCase())) return request;
      if (!request.headers.get(IDEMPOTENCY_HEADER)) {
        request.headers.set(IDEMPOTENCY_HEADER, newKey());
      }
      return request;
    },
  };
}

/** How long a read may take before the app stops waiting and says so. */
export const READ_TIMEOUT_MS = 30_000;

/**
 * A read that never answers ends after thirty seconds (review 2026-10-06: the jobs home waited on a
 * stalled connection for ever, blank, with nothing to tell the person). Only GETs through the typed
 * client: an upload, a setup save or any other write keeps its own time, and the workbench's engine
 * channel and the live stream do not come through this client at all.
 */
export function readTimeout(ms: number = READ_TIMEOUT_MS): Middleware {
  return {
    onRequest({ request }) {
      if (request.method.toUpperCase() !== 'GET') return request;
      const limit = AbortSignal.timeout(ms);
      // Both the caller's own cancel and the limit end the request, where the browser can join them.
      const signal =
        typeof AbortSignal.any === 'function' ? AbortSignal.any([request.signal, limit]) : limit;
      return new Request(request, { signal });
    },
  };
}
